/**
 * Sibling Swatch Popover
 * ----------------------
 * Shows a small cursor-following popover when hovering over the color
 * and strength sibling-product swatches rendered by the `product_variations`
 * block in the product info column.
 *
 * Targets:
 *   - .product-info__product-picker .color-swatch[href]
 *   - .product-info__product-picker .strength-icon-selector
 *
 * Label resolution priority:
 *   1. data-popover-label attribute
 *   2. aria-label attribute (used by strength icons)
 *   3. .sr-only text content (used by color swatches)
 *   4. trimmed text content
 */
(function () {
  'use strict';

  if (window.__siblingSwatchPopoverInit) return;
  window.__siblingSwatchPopoverInit = true;

  // Only initialize on devices that actually support hover (skip touch-only).
  if (typeof window.matchMedia === 'function' && !window.matchMedia('(hover: hover)').matches) {
    return;
  }

  var SELECTOR = [
    '.product-info__product-picker .color-swatch[href]',
    '.product-info__product-picker .strength-icon-selector'
  ].join(', ');

  var CURSOR_OFFSET = 14;
  var EDGE_PADDING = 8;

  var popover = null;
  var currentTarget = null;
  var rafId = null;
  var pointerX = 0;
  var pointerY = 0;

  function ensurePopover() {
    if (popover) return popover;
    popover = document.createElement('div');
    popover.className = 'sibling-swatch-popover';
    popover.setAttribute('role', 'tooltip');
    popover.setAttribute('aria-hidden', 'true');
    document.body.appendChild(popover);
    return popover;
  }

  function resolveLabel(el) {
    var dataLabel = el.getAttribute('data-popover-label');
    if (dataLabel && dataLabel.trim()) return dataLabel.trim();

    var ariaLabel = el.getAttribute('aria-label');
    if (ariaLabel && ariaLabel.trim()) return ariaLabel.trim();

    var sr = el.querySelector('.sr-only');
    if (sr && sr.textContent.trim()) return sr.textContent.trim();

    var text = el.textContent ? el.textContent.trim() : '';
    return text;
  }

  function applyPosition() {
    rafId = null;
    if (!popover || !currentTarget) return;

    var rect = popover.getBoundingClientRect();
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;

    var x = pointerX + CURSOR_OFFSET;
    var y = pointerY + CURSOR_OFFSET;

    if (x + rect.width + EDGE_PADDING > vw) {
      x = pointerX - rect.width - CURSOR_OFFSET;
    }
    if (y + rect.height + EDGE_PADDING > vh) {
      y = pointerY - rect.height - CURSOR_OFFSET;
    }
    if (x < EDGE_PADDING) x = EDGE_PADDING;
    if (y < EDGE_PADDING) y = EDGE_PADDING;

    popover.style.transform = 'translate3d(' + Math.round(x) + 'px, ' + Math.round(y) + 'px, 0)';
  }

  function schedulePosition() {
    if (rafId !== null) return;
    rafId = window.requestAnimationFrame(applyPosition);
  }

  function show(target, x, y) {
    var label = resolveLabel(target);
    if (!label) return;

    var el = ensurePopover();
    el.textContent = label;
    currentTarget = target;
    pointerX = x;
    pointerY = y;

    applyPosition();
    el.setAttribute('aria-hidden', 'false');
    el.classList.add('is-visible');
  }

  function hide() {
    if (rafId !== null) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
    currentTarget = null;
    if (!popover) return;
    popover.classList.remove('is-visible');
    popover.setAttribute('aria-hidden', 'true');
  }

  document.addEventListener('pointerover', function (e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    var target = e.target.closest ? e.target.closest(SELECTOR) : null;
    if (!target || target === currentTarget) return;
    show(target, e.clientX, e.clientY);
  });

  document.addEventListener('pointermove', function (e) {
    if (!currentTarget) return;
    if (e.pointerType && e.pointerType !== 'mouse') return;

    var target = e.target.closest ? e.target.closest(SELECTOR) : null;
    if (target !== currentTarget) {
      hide();
      return;
    }

    pointerX = e.clientX;
    pointerY = e.clientY;
    schedulePosition();
  });

  document.addEventListener('pointerout', function (e) {
    if (!currentTarget) return;
    var leaving = e.target.closest ? e.target.closest(SELECTOR) : null;
    if (leaving !== currentTarget) return;

    var related = e.relatedTarget && e.relatedTarget.closest ? e.relatedTarget.closest(SELECTOR) : null;
    if (related === currentTarget) return;
    hide();
  });

  window.addEventListener('scroll', hide, { passive: true, capture: true });
  window.addEventListener('resize', hide);
  window.addEventListener('blur', hide);
})();
